import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import {
  DatePickerType,
  DatePickerUniversal,
  GridStyles,
  ISimpleGridColumns,
  PageHeader,
  PaginationControl,
  Search,
  SimpleGrid,
  Spinner,
  DropdownInput,
  IOption,
  QuestionTooltip,
} from "component-library";
import { PremiumEntitlements } from "shared-types";
import { doesUserHaveEntitlements } from "shared-auth";
import { dataTestIds } from "data-testids";

import { ReactComponent as WarningIcon } from "component-library/src/images/svg/icons/warning-icon-thin.svg";
import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";
import { ISuggestion, RoleStatus } from "./types";
import { useGetProductionType } from "./hooks/useGetProductionTypes";
import { useSearchSuggestions } from "./hooks/useSearchSuggestions";
import DownloadSuggestionsButton from "./DownloadSuggestionsButton";
import "./styles.scss";
import defaultsWithFallback from "helpers/defaultsWithFallback";
import { fallbackTranslation } from "services/api/fallbackData";
import { secondaryVariantStyles } from "component-library/src/components/Organisms/SimpleGrid/GridStyles";

const styles: GridStyles = {
  ...secondaryVariantStyles,
  headerRow: "h-[60px]",
  headerCell: "bg-transparent h-[60px]",
  bodyCell: "min-h-[60px]",
};

const ITEMS_PER_PAGE = 50;

const MySuggestions = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string>();
  const [dates, setDates] = useState<Date[]>([]);
  const [statusId, setStatusId] = useState<string>("");
  const translations = useSelector<IApplicationState, Record<string, any>>(
    (state) => {
      const { translation } = state;

      return {
        resultStats: translation.common.grid.resultStats,
        ...defaultsWithFallback(
          translation.pages[PageKey.Jobs]?.performer,
          fallbackTranslation[PageKey.Jobs].performer
        ),
      };
    }
  );
  const { productionTypes, productionTypesLoading } = useGetProductionType();

  const searchCriteria = {
    search,
    startDate: dates[0],
    endDate: dates[1],
    skip: (page - 1) * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
    statusId,
  };
  const { suggestions, total, isLoading } =
    useSearchSuggestions(searchCriteria);

  const onSearch = useCallback((val?: string) => {
    setSearch(val);
    setPage(1);
  }, []);

  const onDateChange = useCallback((date?: Date[]) => {
    if (date === undefined) setDates([]);

    if (date?.length === 2) setDates(date);

    setPage(1);
  }, []);

  const onStatusChange = useCallback((option: IOption<string>) => {
    setStatusId(option?.value);
    setPage(1);
  }, []);

  const getProductName = useCallback(
    (typeId: number) => {
      return productionTypes.find(({ id }) => id === typeId)?.name;
    },
    [productionTypes]
  );

  const columns: ISimpleGridColumns<ISuggestion> = {
    productionTitle: {
      type: "string",
      label: translations.productionTitleLabel,
      width: 2,
      formatCell: (value, row) => {
        if (row.confidential) {
          return (
            <div className="flex gap-[10px]">
              {translations.confidentialProduction}
              <QuestionTooltip
                title={translations.confidentialProduction}
                html={translations.confidentialProductionTooltipContent}
              />
            </div>
          );
        }

        return <a href={row._links?.role?.href}>{value}</a>;
      },
    },
    type: {
      type: "string",
      label: translations.typeLabel,
      width: 1,
      formatCell: (value) => getProductName(value),
    },
    role: {
      type: "string",
      label: translations.roleLabel,
      width: 1,
    },
    suggestedBy: {
      type: "string",
      label: translations.sentByLabel,
      width: 1,
    },
    suggested: {
      type: "string",
      label: translations.suggestedDateLabel,
      width: 1,
      formatCell: (date) => format(new Date(date), "dd/MM/yyyy"),
    },
    statusID: {
      type: "number",
      label: translations.roleStatusLabel,
      width: 1,
      formatCell: (value) => {
        if (value === RoleStatus.Open) return translations.roleStatusOpen;

        if (value === RoleStatus.Closed) return translations.roleStatusClosed;

        if (value === RoleStatus.Cast)
          return (
            <div className="flex items-center gap-[7px] text-orange-burnt">
              <WarningIcon />
              {translations.roleStatusCast}
            </div>
          );

        return "";
      },
    },
    _links: "hide",
    confidential: "hide",
  };

  const options = [
    { name: translations.allStatuses, value: "" },
    {
      name: translations.roleStatusOpen,
      value: String(RoleStatus.Open),
    },
    {
      name: translations.roleStatusClosed,
      value: String(RoleStatus.Closed),
    },
    {
      name: translations.roleStatusCast,
      value: String(RoleStatus.Cast),
    },
  ];

  const doesUserHavePremium = doesUserHaveEntitlements(
    PremiumEntitlements.Premium_Yes_No
  );

  return (
    <div className="my-suggestions">
      <PageHeader
        pageTitle={
          <h1 className="text-[1.875rem]">
            {translations.mySuggestionsHeader}
          </h1>
        }
        actionSection={
          <DownloadSuggestionsButton
            searchCriteria={searchCriteria}
            getProductName={getProductName}
            translations={translations}
          />
        }
        bottomSection={
          <div className="flex gap-[30px]">
            <Search
              className="max-w-[50%]"
              placeholder={translations.searchPlaceholder}
              maxLength={SEARCH_MAX_LENGTH}
              onSearch={onSearch}
              onClear={onSearch}
              data-testid={
                dataTestIds.nuJobsApp["MySuggestions.MySuggestions.searchInput"]
              }
            />
            <DatePickerUniversal
              className="w-[25%]"
              type={DatePickerType.Range}
              placeholder={translations.datePlaceholder}
              valueChanged={onDateChange}
            />
            {doesUserHavePremium && (
              <DropdownInput
                className="w-[25%]"
                variant="select"
                placeholder={translations.statusFilterPlaceholder}
                options={options}
                optionSelected={onStatusChange}
              />
            )}
          </div>
        }
      />
      <div className="container">
        <div className="flex items-center justify-between pl-[14px]">
          <PaginationResultStats
            page={page}
            itemsPerPage={ITEMS_PER_PAGE}
            itemsTotal={total}
            textTemplate={translations.resultStats}
          />
          <PaginationControl
            pageCount={Math.ceil(total / ITEMS_PER_PAGE)}
            pageRangeDisplayed={8}
            handlePageChange={(pageNumber) => setPage(pageNumber)}
            selectedPage={page}
          />
        </div>
        {isLoading || productionTypesLoading ? (
          <Spinner inline />
        ) : (
          <SimpleGrid
            data={suggestions}
            columns={columns}
            styles={styles}
            maxHeight="10000000px"
          />
        )}

        <div className="flex justify-end mb-[30px]">
          <PaginationControl
            pageCount={Math.ceil(total / ITEMS_PER_PAGE)}
            pageRangeDisplayed={8}
            handlePageChange={(pageNumber) => setPage(pageNumber)}
            selectedPage={page}
          />
        </div>
      </div>
    </div>
  );
};

export default MySuggestions;
